import { useEffect, useState } from 'react';
import { FileClock, Play, Trash2, Loader2, CircleAlert as AlertCircle, HardHat, Briefcase } from 'lucide-react';
import { listDrafts, deleteDraft, type ContractDraft } from './drafts';

const STEP_LABELS = ['Simulação', 'Dados', 'Endereços', 'Contatos', 'Agenda', 'Documentos', 'Revisão'];

function formatCpf(cpf: string) {
  const d = cpf.replace(/\D/g, '');
  return d.length === 11 ? d.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4') : cpf;
}

export default function DraftsList({ onResume }: { onResume: (draft: ContractDraft) => void }) {
  const [drafts, setDrafts] = useState<ContractDraft[] | null>(null);
  const [error, setError] = useState('');
  const [confirmId, setConfirmId] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    listDrafts()
      .then(setDrafts)
      .catch(() => { setDrafts([]); setError('Não foi possível carregar os rascunhos.'); });
  }, []);

  const handleDelete = async (id: string) => {
    setDeleting(true);
    try {
      await deleteDraft(id);
      setDrafts(prev => (prev || []).filter(d => d.id !== id));
      setConfirmId(null);
    } catch {
      setError('Não foi possível descartar o rascunho. Tente novamente.');
    } finally {
      setDeleting(false);
    }
  };

  if (drafts === null) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 mt-10">
        <Loader2 className="w-4 h-4 animate-spin" /> Carregando rascunhos...
      </div>
    );
  }

  if (drafts.length === 0 && !error) return null;

  return (
    <div className="mt-10">
      <div className="flex items-center gap-2 mb-3">
        <FileClock className="w-5 h-5 text-amber-600" />
        <h2 className="text-lg font-semibold text-gray-900">Rascunhos</h2>
        <span className="text-xs font-medium bg-amber-100 text-amber-700 rounded-full px-2 py-0.5">{drafts.length}</span>
      </div>
      <p className="text-sm text-gray-500 mb-4">Cadastros salvos para concluir depois.</p>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-3 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}

      <div className="space-y-3">
        {drafts.map(d => {
          const isClt = d.client_type === 'clt';
          const Icon = isClt ? HardHat : Briefcase;
          return (
            <div key={d.id} className="bg-white rounded-xl border border-gray-100 shadow-sm p-4 transition-shadow hover:shadow-md">
              <div className="flex items-start gap-3">
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${isClt ? 'bg-blue-100' : 'bg-emerald-100'}`}>
                  <Icon className={`w-5 h-5 ${isClt ? 'text-blue-600' : 'text-emerald-600'}`} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-gray-900 truncate">{d.client_name || 'Cliente sem nome'}</p>
                  <p className="text-xs text-gray-500 mt-0.5">
                    {isClt ? 'CLT' : 'Autônomo'}
                    {d.client_cpf && ` · ${formatCpf(d.client_cpf)}`}
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    Parou em <span className="font-medium text-gray-700">{STEP_LABELS[d.current_step - 1] || 'Simulação'}</span>
                    {' · '}salvo em {new Date(d.updated_at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}
                  </p>
                </div>
              </div>

              {confirmId === d.id ? (
                <div className="mt-3 p-3 bg-red-50 border border-red-100 rounded-lg">
                  <p className="text-sm text-red-800 mb-3">Descartar este rascunho? Essa ação não pode ser desfeita.</p>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setConfirmId(null)}
                      disabled={deleting}
                      className="flex-1 py-2 text-sm font-medium border border-gray-200 bg-white text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                    >
                      Cancelar
                    </button>
                    <button
                      onClick={() => handleDelete(d.id)}
                      disabled={deleting}
                      className="flex-1 py-2 text-sm font-medium bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
                    >
                      {deleting ? 'Descartando...' : 'Descartar'}
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-2 mt-3">
                  <button
                    onClick={() => onResume(d)}
                    className="flex-1 flex items-center justify-center gap-1.5 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Play className="w-4 h-4" /> Continuar
                  </button>
                  <button
                    onClick={() => setConfirmId(d.id)}
                    className="flex items-center justify-center gap-1.5 px-4 py-2 text-sm font-medium border border-gray-200 text-gray-600 rounded-lg hover:bg-red-50 hover:text-red-600 hover:border-red-200 transition-colors"
                  >
                    <Trash2 className="w-4 h-4" /> Descartar
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
